import api from './api';

/** Alinhado a `subscription-service/.../bootstrap.py` (DEFAULT_MODULE_PRICES). Usado se a API falhar ou retornar vazio. */
export const FALLBACK_MODULE_PRICING = [
  { code: 'base', display_name: 'Base (login, org, cadastros mínimos)', price_monthly_cents: 2900, is_active: true },
  { code: 'license', display_name: 'Licenças ambientais', price_monthly_cents: 7900, is_active: true },
  { code: 'process', display_name: 'Processos & tramitação', price_monthly_cents: 4900, is_active: true },
  { code: 'legislation', display_name: 'Legislação & assistente normativo', price_monthly_cents: 5900, is_active: true },
  { code: 'complaint', display_name: 'Ouvidoria / denúncias', price_monthly_cents: 3900, is_active: true },
  { code: 'institution', display_name: 'Órgãos & servidores', price_monthly_cents: 2900, is_active: true },
  { code: 'identity', display_name: 'Identidade (pessoas, clientes, empreendimentos)', price_monthly_cents: 2900, is_active: true },
  { code: 'catalog', display_name: 'Catálogo', price_monthly_cents: 1900, is_active: true },
  { code: 'notification', display_name: 'Notificações (e-mail, in-app, agentes)', price_monthly_cents: 1900, is_active: true },
  { code: 'monitoring', display_name: 'Monitoramento ambiental (mapas e notificações)', price_monthly_cents: 2900, is_active: true },
  { code: 'dashboard', display_name: 'Dashboard & indicadores', price_monthly_cents: 1900, is_active: true },
];

/** Alinhado a `subscription-service/.../bootstrap.py` (DEFAULT_USER_TIERS). */
export const FALLBACK_USER_TIERS = [
  { code: 'ate_10', display_name: 'Até 10 usuários', max_users: 10, price_monthly_cents: 0, sort_order: 10 },
  { code: 'ate_25', display_name: 'Até 25 usuários', max_users: 25, price_monthly_cents: 4900, sort_order: 20 },
  { code: 'ate_50', display_name: 'Até 50 usuários', max_users: 50, price_monthly_cents: 9900, sort_order: 30 },
  { code: 'ate_100', display_name: 'Até 100 usuários', max_users: 100, price_monthly_cents: 18900, sort_order: 40 },
  { code: 'ate_250', display_name: 'Até 250 usuários', max_users: 250, price_monthly_cents: 39900, sort_order: 50 },
  { code: 'ilimitado', display_name: 'Usuários ilimitados', max_users: null, price_monthly_cents: 79900, sort_order: 60 },
];

export function parsePricingModulesResponse(body) {
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.modules)) return body.modules;
  return [];
}

/**
 * Carrega a lista de módulos; se a resposta for vazia, mal formatada ou a requisição falhar, devolve a tabela padrão.
 * @returns {Promise<{ list: object[], fromFallback: boolean }>}
 */
export async function fetchPricingModulesWithFallback() {
  try {
    const { data } = await getPricingModules();
    const list = parsePricingModulesResponse(data);
    if (list.length > 0) {
      return { list, fromFallback: false };
    }
  } catch {
    // subscription-service / gateway fora do ar, ou 4xx/5xx
  }
  return { list: FALLBACK_MODULE_PRICING, fromFallback: true };
}

/**
 * Acesso ao SaaS: módulos contratados, bloqueio por inadimplência.
 * O gateway resolve o tenant via `X-Tenant-Org-Id` (igual às outras rotas).
 */
export async function fetchSubscriptionAccess() {
  const { data } = await api.get('/api/v1/subscriptions/me/access');
  return data;
}

export function getSubscription(organizationId) {
  return api.get(`/api/v1/subscriptions/organization/${organizationId}`);
}

export function patchSubscription(organizationId, body) {
  return api.patch(`/api/v1/subscriptions/organization/${organizationId}`, body);
}

export function getPricingModules() {
  return api.get('/api/v1/pricing/modules');
}

export function postPricingCalculate(body) {
  return api.post('/api/v1/pricing/calculate', body);
}

export function getUserTiers() {
  return api.get('/api/v1/pricing/user-tiers');
}

export function updateUserTier(code, body) {
  return api.put(`/api/v1/pricing/user-tiers/${code}`, body);
}

/** Faixas de usuários; cai no padrão quando o serviço de assinatura não responde. */
export async function fetchUserTiersWithFallback() {
  try {
    const { data } = await getUserTiers();
    const list = parsePricingModulesResponse(data);
    if (list.length > 0) return { list, fromFallback: false };
  } catch {
    // subscription-service / gateway fora do ar
  }
  return { list: FALLBACK_USER_TIERS, fromFallback: true };
}

/** Consumo de vagas do plano: usuários internos ativos × limite da faixa contratada. */
export function fetchUserSeats() {
  return api.get('/api/v1/auth/users/seats').then((r) => r.data);
}

/** Cria assinatura (público: cadastro de tenant+admin). Não use X-Tenant-Org-Id. */
export function createSubscription(body) {
  return api.post('/api/v1/subscriptions', body, { timeout: 60000 });
}

export function fetchPlatformOverview() {
  return api.get('/api/v1/subscriptions/platform/overview').then((r) => r.data);
}

export function getPricingSettings() {
  return api.get('/api/v1/pricing/settings');
}

export function updatePricingSettings(body) {
  return api.put('/api/v1/pricing/settings', body);
}

export function updatePricingModule(code, body) {
  return api.put(`/api/v1/pricing/modules/${code}`, body);
}

export function adminConfirmPayment(organizationId) {
  return api.post(`/api/v1/subscriptions/organization/${organizationId}/admin/confirm-payment`);
}

export function adminPatchSubscriptionStatus(organizationId, status) {
  return api.patch(`/api/v1/subscriptions/organization/${organizationId}/admin`, { status });
}

export function getBillingCheckoutUrl() {
  return api.get('/api/v1/subscriptions/me/billing/checkout-url');
}

export function fetchBillingHome() {
  return api.get('/api/v1/subscriptions/me/billing-home');
}

export function chargeSubscription(method) {
  return api.post('/api/v1/subscriptions/me/billing/charge', { method }, { timeout: 60000 });
}

export function downloadSubscriptionBoleto() {
  return api.get('/api/v1/subscriptions/me/billing/boleto', {
    responseType: 'blob',
    timeout: 60000,
  });
}

export function previewSubscriptionChange(organizationId, body) {
  return api.post(`/api/v1/subscriptions/organization/${organizationId}/changes/preview`, body);
}

export function requestSubscriptionChange(organizationId, body) {
  return api.post(`/api/v1/subscriptions/organization/${organizationId}/changes`, body);
}

export function getSubscriptionChanges(organizationId) {
  return api.get(`/api/v1/subscriptions/organization/${organizationId}/changes`);
}

export function cancelSubscriptionChange(organizationId, changeId) {
  return api.post(`/api/v1/subscriptions/organization/${organizationId}/changes/${changeId}/cancel`);
}

export function fetchPaymentProviders() {
  return api.get('/api/v1/billing/providers');
}

export function patchPaymentProvider(code, body) {
  return api.patch(`/api/v1/billing/providers/${code}`, body);
}

export function fetchOrganizationPaymentEvents(organizationId) {
  return api.get(`/api/v1/subscriptions/organization/${organizationId}/payment-events`);
}

/** Após `createSubscription` (campo `checkout_token`); abre fatura / link no Asaas. */
export function getPublicCheckoutUrl(organizationId, checkoutToken) {
  return api.get('/api/v1/subscriptions/checkout-url', {
    params: { organization_id: organizationId, token: checkoutToken },
    timeout: 45000,
  });
}
